import { MapPin, MousePointerClick, Sparkles } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { type GoogleMapsLibrary, loadGoogleMaps } from '../../lib/google-maps-loader';

export interface JobLocationMapProps {
  latitude: number | null;
  longitude: number | null;
  /** Presence of this prop puts the map in editable mode: draggable pin + click-to-place. Omit for a read-only view. */
  onPinChange?: (latitude: number, longitude: number) => void;
  apiKey?: string;
  className?: string;
  heightClassName?: string;
}

interface PinMarker {
  setMap: (map: unknown) => void;
  setPosition: (pos: { lat: number; lng: number }) => void;
  getPosition: () => { lat: () => number; lng: () => number } | null;
  addListener: (event: string, handler: (e?: unknown) => void) => void;
}

interface PinMap {
  panTo: (pos: { lat: number; lng: number }) => void;
  setZoom: (zoom: number) => void;
  addListener: (
    event: string,
    handler: (e: { latLng: { lat: () => number; lng: () => number } }) => void
  ) => void;
}

const DEFAULT_CENTER = { lat: -33.8688, lng: 151.2093 }; // Sydney, matches JobPlanMap's fallback

/**
 * Single-pin map for one job's location. Read-only by default; pass `onPinChange` to make the
 * pin draggable and let a click anywhere on the map place/move it -- the manual-override path
 * for addresses Google Places can't resolve yet (e.g. brand-new developments), so a job can
 * still be scheduled precisely without a verified geocode.
 */
export function JobLocationMap({
  latitude,
  longitude,
  onPinChange,
  apiKey,
  className,
  heightClassName = 'h-56',
}: JobLocationMapProps) {
  const isEditable = onPinChange !== undefined;

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<PinMap | null>(null);
  const markerRef = useRef<PinMarker | null>(null);
  const onPinChangeRef = useRef(onPinChange);
  const initialCoordsRef = useRef({ latitude, longitude });

  useEffect(() => {
    onPinChangeRef.current = onPinChange;
  }, [onPinChange]);

  useEffect(() => {
    let isMounted = true;
    const { latitude: initialLat, longitude: initialLng } = initialCoordsRef.current;

    loadGoogleMaps(apiKey)
      .then((googleMaps: GoogleMapsLibrary) => {
        if (!isMounted || !mapContainerRef.current) return;

        const center =
          initialLat != null && initialLng != null
            ? { lat: initialLat, lng: initialLng }
            : DEFAULT_CENTER;

        const map = new googleMaps.Map(mapContainerRef.current, {
          center,
          zoom: initialLat != null && initialLng != null ? 16 : 10,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          gestureHandling: 'greedy',
        }) as unknown as PinMap;
        mapInstanceRef.current = map;

        const marker = new googleMaps.Marker({
          position: center,
          map,
          draggable: isEditable,
          visible: initialLat != null && initialLng != null,
          icon: {
            path: googleMaps.SymbolPath.CIRCLE,
            scale: 9,
            fillColor: '#dc2626',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
          },
        }) as unknown as PinMarker;
        markerRef.current = marker;

        if (isEditable) {
          marker.addListener('dragend', () => {
            const pos = marker.getPosition();
            if (pos) onPinChangeRef.current?.(pos.lat(), pos.lng());
          });

          map.addListener('click', (e) => {
            const lat = e.latLng.lat();
            const lng = e.latLng.lng();
            marker.setPosition({ lat, lng });
            marker.setMap(map);
            onPinChangeRef.current?.(lat, lng);
          });
        }
      })
      .catch(() => {
        // Fallback: blank map container with the lat/lng readout below still shown.
      });

    return () => {
      isMounted = false;
      markerRef.current?.setMap(null);
      markerRef.current = null;
      mapInstanceRef.current = null;
    };
  }, [apiKey, isEditable]);

  // Keep the marker/center synced when latitude/longitude change from outside (e.g. autocomplete selection).
  useEffect(() => {
    const map = mapInstanceRef.current;
    const marker = markerRef.current;
    if (!map || !marker) return;

    if (latitude != null && longitude != null) {
      const pos = { lat: latitude, lng: longitude };
      marker.setPosition(pos);
      marker.setMap(map);
      map.panTo(pos);
      map.setZoom(16);
    } else {
      marker.setMap(null);
    }
  }, [latitude, longitude]);

  const hasCoords = latitude != null && longitude != null;

  return (
    <div className={className}>
      <div
        ref={mapContainerRef}
        className={`w-full ${heightClassName} rounded-lg border border-border/60 bg-muted/30`}
      />
      <div className="flex items-center justify-between gap-3 mt-1.5 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1">
          {isEditable ? (
            <>
              <MousePointerClick className="h-3 w-3" />
              <span>Drag the pin or click the map to set the exact location.</span>
            </>
          ) : hasCoords ? (
            <>
              <MapPin className="h-3 w-3" />
              <span>Pinned location</span>
            </>
          ) : (
            <>
              <Sparkles className="h-3 w-3" />
              <span>No coordinates pinned yet.</span>
            </>
          )}
        </span>
        {hasCoords && (
          <span className="font-mono shrink-0">
            {latitude!.toFixed(5)}, {longitude!.toFixed(5)}
          </span>
        )}
      </div>
    </div>
  );
}
